import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, MockBadge, StatusBadge, Spinner, EmptyState, fmtMoney } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Send, CheckCircle2 } from "lucide-react";

export default function BGIOF() {
  const { user } = useAuth();
  const [txns, setTxns] = useState(null);
  const [types, setTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [idemKey, setIdemKey] = useState(crypto.randomUUID());
  const [form, setForm] = useState({ customer_id: "", type: "subscription", scheme: "BGIOF Global Growth Fund", amount: "", currency: "USD" });
  const canCreate = ["operations", "relationship_manager", "administrator", "customer"].includes(user.role);
  const isCustomer = user.role === "customer";
  const canApprove = ["operations", "compliance", "administrator"].includes(user.role);

  const load = () => { api.get("/bgiof/transactions").then((r) => setTxns(r.data)); if (!isCustomer) api.get("/bgiof/logs").then((r) => setLogs(r.data)).catch(() => {}); };
  useEffect(() => {
    load(); api.get("/bgiof/types").then((r) => setTypes(r.data.types));
    if (!isCustomer) api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);
  if (!txns) return <Spinner />;

  const create = async () => {
    try {
      const r = await api.post("/bgiof/transactions", { ...form, customer_id: isCustomer ? "self" : form.customer_id, amount: parseFloat(form.amount) || null, idempotency_key: idemKey });
      if (r.data.duplicate_prevented) toast.warning("Duplicate submission prevented — existing transaction returned");
      else toast.success(`${r.data.transaction.ref} submitted`);
      setOpen(false); setIdemKey(crypto.randomUUID()); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const approve = async (id, approved) => { await api.post(`/bgiof/transactions/${id}/approve`, { approved, reason: approved ? "Approved" : "Rejected by reviewer" }); toast.success("Updated"); load(); refresh(id); };
  const submitExt = async (id) => { try { await api.post(`/bgiof/transactions/${id}/submit-external`); toast.success("Submitted to BGIOF (mock)"); load(); refresh(id); } catch (e) { toast.error(apiErr(e)); } };
  const refresh = async (id) => { const r = await api.get("/bgiof/transactions"); setActive(r.data.find((t) => t.id === id)); };

  return (
    <div>
      <PageHeader overline="Transactions" title="BGIOF Transactions"
        subtitle="Simulated BGIOF workflow with configurable types. BGIOF has NO confirmed API — real connectivity requires its system details, access and tech-team coordination."
        testid="bgiof-header"
        actions={canCreate && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setIdemKey(crypto.randomUUID()); }}>
            <DialogTrigger asChild><Button className="gap-1" data-testid="new-bgiof-btn"><Plus className="h-4 w-4" /> New Transaction</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="flex items-center gap-2">New BGIOF Transaction <MockBadge /></DialogTitle></DialogHeader>
              <div className="space-y-3">
                {!isCustomer && (
                <div><Label>Customer</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger data-testid="bgiof-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name} ({c.compliance_status})</SelectItem>)}</SelectContent>
                  </Select></div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="bgiof-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Currency</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["USD", "AED", "INR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div><Label>Scheme</Label><Input value={form.scheme} onChange={(e) => setForm({ ...form, scheme: e.target.value })} /></div>
                <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bgiof-amount" /></div>
                <p className="text-[11px] text-muted-foreground">Eligibility/KYC is checked automatically. Idempotency key prevents duplicate submissions.</p>
              </div>
              <DialogFooter><Button onClick={create} disabled={!isCustomer && !form.customer_id} data-testid="submit-bgiof-btn">Submit</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      <Tabs defaultValue="txns">
        <TabsList><TabsTrigger value="txns" data-testid="tab-txns">Transactions</TabsTrigger>{!isCustomer && <TabsTrigger value="logs" data-testid="tab-logs">Data-exchange Logs</TabsTrigger>}</TabsList>
        <TabsContent value="txns" className="mt-4">
          <Card className="p-5">
            {txns.length === 0 ? <EmptyState title="No transactions" /> : (
              <Table><TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Customer</TableHead><TableHead>Type</TableHead><TableHead>Scheme</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>KYC</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>{txns.map((t) => (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => setActive(t)} data-testid={`bgiof-row-${t.ref}`}>
                    <TableCell className="font-num text-xs">{t.ref}</TableCell><TableCell>{t.customer_name}</TableCell><TableCell className="capitalize">{t.type}</TableCell>
                    <TableCell className="text-xs">{t.scheme}</TableCell><TableCell className="text-right font-num">{fmtMoney(t.amount, t.currency)}</TableCell>
                    <TableCell><StatusBadge status={t.kyc_check?.status} /></TableCell><TableCell><StatusBadge status={t.status} /></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
        {!isCustomer && (
        <TabsContent value="logs" className="mt-4">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3"><h3 className="font-semibold">External data-exchange logs</h3><MockBadge /></div>
            {logs.length === 0 ? <EmptyState title="No logs yet" /> : logs.map((l) => (
              <div key={l.id} className="text-xs border border-border rounded-lg p-3 mb-2">
                <div className="flex justify-between"><span className="font-medium">{l.ref} → {l.system}</span><span className="text-muted-foreground">{new Date(l.ts).toLocaleString()}</span></div>
                <div className="text-muted-foreground mt-1">Response: {l.response?.external_ref} · {l.response?.decision}</div>
              </div>
            ))}
          </Card>
        </TabsContent>
        )}
      </Tabs>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {active && (
            <>
              <SheetHeader><SheetTitle>{active.ref} · {active.type}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{active.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-num">{fmtMoney(active.amount, active.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">KYC</span><StatusBadge status={active.kyc_check?.status} /></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={active.status} /></div>
                {active.external_ack && <div className="flex justify-between"><span className="text-muted-foreground">External ref</span><span className="font-num text-xs">{active.external_ack.external_ref}</span></div>}
                <div className="pt-3 border-t border-border">
                  <h4 className="font-semibold mb-2">Status history</h4>
                  {(active.status_history || []).map((h, i) => (
                    <div key={i} className="text-xs border-l-2 border-border pl-2 py-1"><span className="font-medium capitalize">{h.status}</span> · {h.by} · {new Date(h.ts).toLocaleString()} {h.reason ? `· ${h.reason}` : ""}</div>
                  ))}
                </div>
                {canApprove && active.status === "pending_approval" && (
                  <div className="flex gap-2 pt-3">
                    <Button size="sm" className="gap-1" onClick={() => approve(active.id, true)} data-testid="bgiof-approve"><CheckCircle2 className="h-4 w-4" /> Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => approve(active.id, false)} data-testid="bgiof-reject">Reject</Button>
                  </div>
                )}
                {active.status === "approved" && (
                  <Button size="sm" className="gap-1 mt-2" onClick={() => submitExt(active.id)} data-testid="bgiof-submit-external"><Send className="h-4 w-4" /> Submit to BGIOF (mock)</Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
